import { CommonModule } from '@angular/common';
import { Component, OnDestroy, computed, effect, inject, untracked } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/material.module';
import { AdminSidePanelService } from '@shared/utils/admin-side-panel.service';
import { LocationContextService } from '@core/tenant/location-context.service';
import { PosOperationalContextService } from '../../../services/pos-operational-context.service';
import {
  PosStaffPinDialogComponent,
  PosStaffPinDialogMode,
} from '../../dialogs/pos-staff-pin.dialog';
import { PosTipShiftDialogComponent } from '../../dialogs/pos-tip-shift.dialog';
import { PosShiftDialogComponent } from '../../dialogs/pos-shift.dialog';

@Component({
  selector: 'app-pos-header-ops',
  standalone: true,
  imports: [CommonModule, MaterialModule, TranslateModule],
  templateUrl: './pos-header-ops.component.html',
  styleUrl: './pos-header-ops.component.scss',
})
export class PosHeaderOpsComponent implements OnDestroy {
  private readonly ops = inject(PosOperationalContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly dialog = inject(MatDialog);
  private readonly sidePanel = inject(AdminSidePanelService);
  private pinDialogRef: MatDialogRef<PosStaffPinDialogComponent, boolean> | null = null;

  readonly staff = this.ops.staff;
  readonly tipShiftSetupIncomplete = this.ops.tipShiftSetupIncomplete;
  readonly tipShiftActive = computed(
    () => this.ops.tipShiftTrackingEnabled() && this.ops.tipShiftOpen()
  );
  readonly tipShiftBlocked = computed(
    () => this.ops.tipShiftTrackingEnabled() && !this.ops.canCharge()
  );
  readonly tipShiftButtonLabelKey = computed(() => {
    if (this.tipShiftSetupIncomplete()) {
      return 'pos.tipShift.setupShort';
    }
    if (this.tipShiftActive()) {
      return 'pos.tipShift.activeShort';
    }
    if (this.tipShiftBlocked()) {
      return 'pos.tipShift.startShort';
    }
    return 'pos.tipShift.short';
  });
  readonly tipShiftButtonIcon = computed(() => {
    if (this.tipShiftSetupIncomplete()) {
      return 'tune';
    }
    if (this.tipShiftActive()) {
      return 'check_circle';
    }
    if (this.tipShiftBlocked()) {
      return 'schedule';
    }
    return 'schedule';
  });

  readonly shiftRequired = this.ops.shiftRequired;
  readonly cashShiftOpen = computed(() => this.ops.shiftRequired() && this.ops.shiftOpen());
  readonly cashShiftBlocked = computed(() => this.ops.shiftRequired() && !this.ops.shiftOpen());
  readonly cashShiftButtonLabelKey = computed(() =>
    this.cashShiftOpen() ? 'pos.cashShift.activeShort' : 'pos.cashShift.openShort'
  );
  readonly cashShiftButtonIcon = computed(() => (this.cashShiftOpen() ? 'point_of_sale' : 'schedule'));

  constructor() {
    effect(() => {
      const locationId = this.locationContext.activeLocationId();
      untracked(() => {
        if (locationId == null) {
          return;
        }
        this.ops.refresh().subscribe({ error: () => undefined });
      });
    });

    effect(() => {
      const needsLock = this.ops.lockRequired() && !this.ops.staff();
      if (!needsLock) {
        return;
      }
      untracked(() => this.openStaffPin('locked'));
    });
  }

  ngOnDestroy(): void {
    if (this.pinDialogRef) {
      this.pinDialogRef.close();
      this.pinDialogRef = null;
    }
  }

  openTipShift(): void {
    this.dialog.open(PosTipShiftDialogComponent, {
      width: '440px',
      maxWidth: '95vw',
      autoFocus: false,
      panelClass: 'pos-tip-shift-panel',
    });
  }

  openCashShift(): void {
    this.sidePanel.open(PosShiftDialogComponent, {
      panelClass: 'pos-shift-panel',
    });
  }

  openStaffPin(mode: PosStaffPinDialogMode = 'identify'): void {
    if (this.pinDialogRef) {
      return;
    }
    this.pinDialogRef = this.dialog.open(PosStaffPinDialogComponent, {
      width: '400px',
      maxWidth: '95vw',
      disableClose: mode === 'locked',
      autoFocus: false,
      panelClass: 'pos-staff-pin-panel',
      data: { mode },
    });
    this.pinDialogRef.afterClosed().subscribe(() => {
      this.pinDialogRef = null;
      if (this.ops.lockRequired() && !this.ops.staff()) {
        queueMicrotask(() => this.openStaffPin('locked'));
      }
    });
  }

  changeOperator(): void {
    // No limpiar antes: si cancela el PIN, conserva el operador actual.
    this.openStaffPin('identify');
  }

  onOperatorClick(): void {
    if (this.staff()) {
      this.changeOperator();
    }
  }
}
